import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { isGloballyHandled } from '../../services/api';
import { recordStartingNotice, signUpForCircle } from '../../services/circles.service';
import { useActiveCohortSessionsStore } from '../../stores/active-cohort-sessions.store';
import { useUpgradePromptStore } from '../../stores/upgrade-prompt.store';
import type { StartingNoticeEvent, StartingSoon } from '../../types/startingSoon';
import { trackGaEvent } from '../../utils/ga';
import { Z_BANNER } from '../../utils/layers';
import { useAppNoticesQuiet } from '../notices/quietZone';
import { useBelowHeaderHost } from './belowHeader';
import { endsNotice, listenToOtherTabs, useCircleNoticeOutcomes } from './circleNoticeOutcomes';
import { goJustMissed } from './justMissed';
import { deviceClock } from '../../utils/booking';
import { countOf } from '../../utils/plural';

/** All of the notice's words (no i18n layer; the NOTICE_COPY pattern). No dashes. */
export const STARTING_SOON_COPY = {
  title: (minutes: number) =>
    minutes <= 0
      ? 'An empathy circle is starting now'
      : `An empathy circle starts in ${minutes} min`,
  srTitle: (time: string) => `An empathy circle starts at ${time}`,
  people: (n: number) => `${countOf(n, 'person', 'people')} joining`,
  join: 'Join',
  notNow: 'Not now',
  joining: 'Joining…',
  joined: (minutes: number) =>
    minutes <= 0 ? "You're in. Starting now" : `You're in. Starts in ${minutes} min`,
  failed: 'Could not sign you up. Please try again from the Circles tab.',
} as const;

/** How long "You're in" stays before the card leaves (the start banner takes over at :00). */
export const JOINED_HOLD_MS = 3_000;

type Phase =
  | { kind: 'offer' }
  | { kind: 'joining' }
  | { kind: 'joined' }
  /** About one circle (`key`): it lasts while that circle is still the one offered. */
  | { kind: 'error'; message: string; key: number | string }
  | { kind: 'gone' };

function subscribeVisibility(cb: () => void): () => void {
  document.addEventListener('visibilitychange', cb);
  return () => document.removeEventListener('visibilitychange', cb);
}

/** Only a visible tab renders the notice (§6). */
export function useTabVisible(): boolean {
  return useSyncExternalStore(
    subscribeVisibility,
    () => document.visibilityState !== 'hidden',
    () => true,
  );
}

/** The server's clock now (N4): the device clock plus the offset the last poll measured. */
function useServerNow(offsetMs: number, wakeAt: number | null): number {
  const [now, setNow] = useState(() => Date.now() + offsetMs);
  useEffect(() => {
    setNow(Date.now() + offsetMs);
    // The countdown text changes at most once a minute.
    const every = setInterval(() => setNow(Date.now() + offsetMs), 60_000);
    // And exactly at the expiry, between polls.
    const at = wakeAt === null ? null : wakeAt - (Date.now() + offsetMs);
    const once =
      at !== null && at > 0 ? setTimeout(() => setNow(Date.now() + offsetMs), at + 50) : null;
    return () => {
      clearInterval(every);
      if (once) clearTimeout(once);
    };
  }, [offsetMs, wakeAt]);
  return now;
}

function minutesUntil(iso: string, now: number): number {
  return Math.ceil((Date.parse(iso) - now) / 60_000);
}

/**
 * The Join actions this build knows (StartingSoonAction). Any other kind is
 * not offered: join() would otherwise fall through to its last branch, a
 * signup for "the next hour from now", a circle nobody chose (N14). An
 * unmapped kind is false, never another kind's door (AGENTS.md).
 */
function knownAction(action: StartingSoon['action'] | undefined): boolean {
  switch (action?.kind) {
    case 'signup':
    case 'enter':
    case 'open_group':
    case 'credits':
    case 'register':
      return true;
    default:
      return false;
  }
}

/** The notice's once-per-circle identity: the circle id, or a signed-out key. */
export function noticeKeyOf(n: StartingSoon): number | string {
  return n.notice_key ?? n.circle_id;
}

/**
 * The fourth and lowest-priority variant of CircleStartBanner: "An empathy
 * circle starts in 10 min" for a circle this person is not signed up for
 * (docs/plans/CIRCLE-STARTING-NOTICE.md §6, N9). The server decides WHICH
 * circle and WHAT Join does (`starting_soon`); this decides only WHEN to show:
 * nothing of higher priority up (`blocked`), no quiet zone, a visible tab, the
 * server clock before `expires_at`, and not already ended in any tab.
 */
export default function StartingSoonCard({
  blocked,
  pathname,
  onSignedUp,
}: {
  blocked: boolean;
  pathname: string;
  /** After a successful signup, where nothing on this page will take them in at T (a public page). */
  onSignedUp?: () => void;
}) {
  const notice = useActiveCohortSessionsStore((s) => s.startingSoon);
  const offsetMs = useActiveCohortSessionsStore((s) => s.serverClockOffsetMs);
  return (
    <StartingSoonView
      notice={notice}
      offsetMs={offsetMs}
      blocked={blocked}
      pathname={pathname}
      signedIn
      onSignedUp={onSignedUp}
    />
  );
}

/**
 * The card itself, fed by a source: the signed-in poll (above) or, signed
 * out, PublicStartingSoonHost (§5). Signed out it writes no server rows (S6):
 * the outcome store (channel + localStorage) is the once-per-circle record.
 */
export function StartingSoonView({
  notice,
  offsetMs,
  blocked,
  pathname,
  signedIn,
  onRegister,
  onSignedUp,
}: {
  notice: StartingSoon | null;
  offsetMs: number;
  blocked: boolean;
  pathname: string;
  signedIn: boolean;
  /** Signed out: called on Join before navigating to /register (the host saves the pending join). */
  onRegister?: (notice: StartingSoon) => void;
  /** Signed in: called once the signup succeeded (StartingSoonCard's prop). */
  onSignedUp?: () => void;
}) {
  const navigate = useNavigate();
  const record = useCallback(
    (circleId: number, event: StartingNoticeEvent): void => {
      // S6: no server rows for signed-out visitors.
      if (!signedIn) return;
      // Fire and forget: the notice never waits on its own analytics.
      recordStartingNotice(circleId, event).catch(() => {});
    },
    [signedIn],
  );
  const outcomes = useCircleNoticeOutcomes((s) => s.outcomes);
  const quiet = useAppNoticesQuiet(pathname);
  const tabVisible = useTabVisible();
  const reduceMotion = useReducedMotion();
  const now = useServerNow(offsetMs, notice ? Date.parse(notice.expires_at) : null);

  // The notice the card is holding: a Join in flight or its success stays on
  // screen even after the next poll drops the circle (it is theirs now). An
  // error does not hold: it shows only while its circle is still offered and
  // before expires_at, so a failed Join never outlives its circle or covers a
  // newer one (Codex on #1099).
  const [held, setHeld] = useState<StartingSoon | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'offer' });
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => listenToOtherTabs(), []);
  useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  // A different circle (or none) starts fresh; so does an error about another circle.
  const noticeId = notice ? noticeKeyOf(notice) : null;
  useEffect(() => {
    setPhase((p) =>
      p.kind === 'offer' || p.kind === 'gone' || (p.kind === 'error' && p.key !== noticeId)
        ? { kind: 'offer' }
        : p,
    );
  }, [noticeId]);

  // Placement A: just below the header, resting under the safe area once the
  // header scrolls away (belowHeader.ts).
  // One ref callback per card, keyed by circle (the AnimatePresence handover,
  // belowHeader.ts useBelowHeaderHost).
  const hostRefFor = useBelowHeaderHost(pathname);

  const busy = phase.kind === 'joining' || phase.kind === 'joined';
  const current = busy ? held : notice;
  // The error, only on the circle it is about (the reset above lands a render later).
  const errorHere = phase.kind === 'error' && !!current && noticeKeyOf(current) === phase.key;
  const view: Phase = phase.kind === 'error' && !errorHere ? { kind: 'offer' } : phase;

  const eligible =
    !!current &&
    knownAction(current.action) &&
    !blocked &&
    !quiet &&
    tabVisible &&
    (busy ||
      (now < Date.parse(current.expires_at) &&
        // A failed Join marked the circle `clicked`; its error still shows.
        (errorHere || !endsNotice(outcomes[noticeKeyOf(current)]))));
  const show = eligible && view.kind !== 'gone';

  // Record `shown` once per circle (not per poll): the outcome store remembers
  // it across tabs and reloads, the server row across devices.
  useEffect(() => {
    if (!show || !current || busy) return;
    if (useCircleNoticeOutcomes.getState().outcomes[noticeKeyOf(current)] !== undefined) return;
    useCircleNoticeOutcomes.getState().mark(noticeKeyOf(current), 'shown');
    record(current.circle_id, 'shown');
    trackGaEvent('circle_notice_shown', { circle_kind: current.kind });
  }, [show, current, busy, record]);

  if (!current) return <AnimatePresence />;

  const dismiss = () => {
    useCircleNoticeOutcomes.getState().mark(noticeKeyOf(current), 'dismissed');
    record(current.circle_id, 'dismissed');
    trackGaEvent('circle_notice_dismissed', { circle_kind: current.kind });
    setPhase({ kind: 'gone' });
  };

  const join = async () => {
    const action = current.action;
    trackGaEvent('circle_notice_clicked', { circle_kind: current.kind, action: action.kind });

    if (action.kind === 'credits') {
      // The existing out-of-credits prompt and nothing else: no signup, no
      // join. It does not end the notice; after a top-up the next poll
      // offers the real Join.
      useUpgradePromptStore.getState().show('credits', action.details);
      record(current.circle_id, 'upgrade_prompted');
      return;
    }

    useCircleNoticeOutcomes.getState().mark(noticeKeyOf(current), 'clicked');
    record(current.circle_id, 'clicked');

    // Signed out: register with the circle preselected (§5); the pending
    // join carries it through verification.
    if (action.kind === 'enter' || action.kind === 'open_group' || action.kind === 'register') {
      if (action.kind === 'register') onRegister?.(current);
      setPhase({ kind: 'gone' });
      void navigate(action.path);
      return;
    }

    setHeld(current);
    setPhase({ kind: 'joining' });
    try {
      await signUpForCircle(action.expected_scheduled_at);
      setPhase({ kind: 'joined' });
      holdTimer.current = setTimeout(() => setPhase({ kind: 'gone' }), JOINED_HOLD_MS);
      void useActiveCohortSessionsStore.getState().refresh();
      onSignedUp?.();
    } catch (e) {
      // A 402 was answered by the global out-of-credits prompt: nothing to add.
      if (isGloballyHandled(e)) {
        setPhase({ kind: 'gone' });
        return;
      }
      const res = (
        e as {
          response?: {
            status?: number;
            data?: { error?: string; message?: string; next_scheduled_at?: string };
          };
        }
      ).response;
      if (res?.status === 409 && res.data?.error === 'missed') {
        setPhase({ kind: 'gone' });
        goJustMissed(navigate, res.data.next_scheduled_at ?? null);
        return;
      }
      setPhase({
        kind: 'error',
        message: res?.data?.message || STARTING_SOON_COPY.failed,
        key: noticeKeyOf(current),
      });
    }
  };

  const minutes = minutesUntil(current.starts_at, now);
  const startTime = deviceClock(current.starts_at);
  const secondary = [
    current.name,
    current.people_joining ? STARTING_SOON_COPY.people(current.people_joining) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          key={noticeKeyOf(current)}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
          ref={hostRefFor(noticeKeyOf(current))}
          className="ee-session-start-host ee-session-start-host--below-header"
          data-testid="circle-starting-soon-host"
          style={{ zIndex: Z_BANNER }}
        >
          {/* biome-ignore lint/a11y/noStaticElementInteractions: Escape inside the card dismisses it; the card itself is not a control. */}
          <section
            role="status"
            aria-live="polite"
            data-testid="circle-starting-soon"
            className="ee-card ee-card--pad-sm ee-session-start ee-session-start--soon"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && view.kind === 'offer') dismiss();
            }}
          >
            <div className="ee-session-start__text">
              <p className="ee-session-start__title">
                {/* The countdown is visual; the announcement is the start time, once. */}
                <span aria-hidden="true">
                  {view.kind === 'joined'
                    ? STARTING_SOON_COPY.joined(minutes)
                    : STARTING_SOON_COPY.title(minutes)}
                </span>
                <span className="sr-only">
                  {view.kind === 'joined'
                    ? STARTING_SOON_COPY.joined(minutes)
                    : STARTING_SOON_COPY.srTitle(startTime)}
                </span>
              </p>
              {view.kind === 'error' ? (
                <p
                  className="ee-session-start__body ee-session-start__error"
                  data-testid="circle-starting-soon-error"
                >
                  {view.message}
                </p>
              ) : (
                <p className="ee-session-start__body">{secondary}</p>
              )}
            </div>
            {view.kind === 'offer' || view.kind === 'joining' ? (
              <div className="ee-session-start__actions">
                <button
                  type="button"
                  onClick={dismiss}
                  disabled={view.kind === 'joining'}
                  className="ee-button ee-button--ghost ee-button--sm ee-session-start__later"
                >
                  {STARTING_SOON_COPY.notNow}
                </button>
                <button
                  type="button"
                  onClick={() => void join()}
                  disabled={view.kind === 'joining'}
                  className="ee-button ee-button--primary ee-button--sm"
                >
                  {view.kind === 'joining' ? STARTING_SOON_COPY.joining : STARTING_SOON_COPY.join}
                </button>
              </div>
            ) : view.kind === 'error' ? (
              <div className="ee-session-start__actions">
                <button
                  type="button"
                  onClick={() => setPhase({ kind: 'gone' })}
                  className="ee-button ee-button--ghost ee-button--sm ee-session-start__later"
                >
                  {STARTING_SOON_COPY.notNow}
                </button>
              </div>
            ) : null}
          </section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
